import { filterBy } from '@progress/kendo-data-query';
import { get } from 'lodash';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
    ComboBox,
    ComboBoxBlurEvent,
    ComboBoxChangeEvent,
    ComboBoxFilterChangeEvent,
    ListItemProps,
} from '@components/dropdowns';

const Styles = styled.div.attrs((props) => ({ className: 'app-autocomplete' }))`
    &&&.app-autocomplete {
        font-size: 1em;

        .k-combobox {
            font-size: 0.86em;
            background: transparent;
        }

        &.hide-select-tick {
            .k-select {
                display: none;
            }
        }
    }
`;

const GlobalStyles = createGlobalStyle`
    .app-autocomplete-popup {
        &&& {
            font-size: var(--font-size) !important;
            width: unset !important;

            .k-popup {
                font-size: 1em;
                border-radius: 0;
            }

            .k-list-item {
                padding: 0 1.14em !important;

                .autocomplete-item {
                    display: flex;
                    align-items: center;
                    width: 100%;

                    font-style: normal !important;
                    font-size: 0.86em !important;
                    line-height: 1.071em !important;
                    color: #3A4B68 !important;

                    .autocomplete-item-text {
                        //flex: 1 1 auto;
                    }

                    .autocomplete-item-hint {
                        padding-left: 0.36em;
                        font-size: 0.71em !important;
                        color: #8F97B0;
                    }
                }
            }
        }
    }
`;

export type AutoCompleteProps = {
    items: any[];
    value: any;
    valueProp?: string;
    textProp?: string;
    hintProp?: string;
    pageSize?: number;
    onBlur?: (e: any) => void;
    onChange: (value: any) => void;
    disabled?: boolean;
    className?: string;
    popupClassName?: string;
    showTick?: boolean;
    allowCustom?: boolean;
    name?: string;
};

type OptionItem = {
    id: any;
    text: string;
    hint: string;
    customItem: boolean;
};

export function AutoComplete({
    items: originalItems = [],
    value,
    valueProp,
    textProp,
    hintProp,
    disabled,
    allowCustom,
    pageSize = 10,
    onChange,
    onBlur,
    className,
    popupClassName,
    showTick = true,
    name,
}: AutoCompleteProps) {
    const [opened, setOpened] = useState<boolean>(false);
    const [filterText, setFilterText] = useState<any>('');
    const [pageInfo, setPageInfo] = useState({ skip: 0, take: pageSize });

    const [filteredItems, setFilteredItems] = useState<OptionItem[]>([]);

    function updateFilteredItems(filterText: string) {
        setFilterText(filterText);

        const filtered = filterBy(originalItems, {
            field: textProp,
            value: filterText,
            operator: 'contains',
            ignoreCase: true,
        }).map((option) => makeOptionItem(option));

        if (allowCustom && filtered.length === 0 && filterText.trim().length > 0) {
            if (!filtered.some((item) => item.text === filterText)) {
                filtered.push({
                    id: filterText,
                    text: filterText,
                    customItem: true,
                } as ReturnType<typeof makeOptionItem>);
            }
        }

        setFilteredItems(filtered);
    }

    function handleFilterChange(event: ComboBoxFilterChangeEvent) {
        updateFilteredItems(event.filter.value);
        setPageInfo((p) => ({ ...p, skip: 0 }));
    }

    const [valueItem, setValueItem] = useState<any>();

    useEffect(() => {
        const options = originalItems?.map(makeOptionItem);
        const newItem = options?.find((d) => d.id === value) || null;
        setValueItem(newItem);
        updateFilteredItems(newItem?.text || (allowCustom && typeof value === 'string' && value) || '');
        // eslint-disable-next-line
    }, [originalItems, value]);

    const pagedData = useMemo(() => {
        const paged = filteredItems.slice(pageInfo.skip, pageInfo.skip + pageInfo.take);
        return paged;
    }, [filteredItems, pageInfo]);

    const ref = useRef<any>();

    useEffect(() => {
        const clickHandler = () => setOpened(true);

        const element = ref.current.element;
        element.querySelector('input').addEventListener('click', clickHandler);
        return () => element?.removeEventListener('click', clickHandler);
    }, []);

    useEffect(() => {
        ref.current.element.querySelector('input').name = name;
    }, [name]);

    function handleChange(e: ComboBoxChangeEvent) {
        if (!opened) {
            onChange && onChange(e.value?.id);
        }

        setFilterText(e.value?.text || (allowCustom && filterText.trim()) || '');
        // setOpened(false);
        setValueItem(e.value);
    }

    function onFocus() {
        setOpened(true);
    }

    function handleBlur(e: ComboBoxBlurEvent) {
        setOpened(false);
        onBlur && onBlur(e);

        const foundItem = filteredItems.find((item) => item.text === filterText);
        const newValue = allowCustom ? foundItem?.id || valueItem?.id || filterText.trim() : valueItem?.id;
        onChange && onChange(newValue);
    }

    function handleOpen() {
        setOpened(true);
        updateFilteredItems(valueItem?.text || '');
    }

    function onClose(e: ComboBoxBlurEvent) {
        setOpened(false);
    }

    function makeOptionItem(value: any): OptionItem {
        return {
            id: !valueProp ? value : get(value, valueProp),
            text: !textProp ? value : get(value, textProp),
            hint: hintProp && get(value, hintProp),
            customItem: false,
        };
    }

    function itemRenderer(li: ReactElement<HTMLLIElement>, itemProps: ListItemProps) {
        const content = (
            <div className="autocomplete-item">
                <span className="autocomplete-item-text">{li.props.children as any}</span>
                <span className="autocomplete-item-hint">{itemProps.dataItem.hint}</span>
            </div>
        );

        return React.cloneElement(li, li.props, content);
    }

    return (
        <Styles className={`app-autocomplete ${showTick !== false ? '' : 'hide-select-tick'} ${className || ''}`}>
            <GlobalStyles />
            <ComboBox
                data={pagedData}
                textField="text"
                dataItemKey="id"
                allowCustom={allowCustom}
                filterable={true}
                filter={filterText}
                onFilterChange={handleFilterChange}
                value={valueItem}
                itemRender={itemRenderer}
                opened={opened}
                onFocus={onFocus}
                onBlur={handleBlur}
                onOpen={handleOpen}
                onClose={onClose}
                onChange={handleChange}
                popupSettings={{
                    animate: false,
                    className: `app-autocomplete-popup ${popupClassName || ''}`,
                }}
                listNoDataRender={() => null}
                disabled={disabled}
                clearButton={false}
                virtual={{ pageSize: pageSize, skip: pageInfo.skip, total: filteredItems.length }}
                onPageChange={(e) => setPageInfo(e.page)}
                // @ts-ignore
                ref={ref as any}
            />
        </Styles>
    );
}
